import type { ReactNode } from 'react';
import { getCoveragePercent } from './backtestStatusFormatters';
import type { StrategyCoverage, StrategyQueueStatus, StrategyScorecardContext } from './backtestStatusTypes';

interface BacktestHealthStripProps {
    coverage: StrategyCoverage;
    queueStatus: StrategyQueueStatus | undefined;
    scorecardContext: StrategyScorecardContext | undefined;
    onViewMissing: () => void;
}

const StatCard = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="rounded-lg bg-zinc-900/60 p-4">
        <div className="text-xs text-zinc-500">{label}</div>
        {children}
    </div>
);

const coverageBarClass = (percent: number): string => {
    if (percent >= 90) return 'bg-green-500';
    if (percent >= 50) return 'bg-amber-500';

    return 'bg-red-500';
};

const getSegmentWidth = (count: number, total: number): string => `${total > 0 ? (count / total) * 100 : 0}%`;

const BacktestHealthStrip = ({ coverage, queueStatus, scorecardContext, onViewMissing }: BacktestHealthStripProps) => {
    const entityCoveragePercent = getCoveragePercent(coverage.entitiesWithBacktestsCount, coverage.totalEntitiesInUniverse);
    const activeQueueCount = queueStatus ? queueStatus.byStatus.reduce((sum, row) => sum + row.count, 0) : 0;
    const isStale = Boolean(scorecardContext?.hasBacktests && scorecardContext.hasStaleBacktests);
    const population = coverage.populationProgress;

    const populationStatus = [
        population.activeBacktests > 0 ? `${population.activeBacktests} active` : null,
        population.failedBacktests > 0 ? `${population.failedBacktests} failed` : null,
        population.unqueuedBacktests > 0 ? `${population.unqueuedBacktests} not queued` : null
    ]
        .filter((status): status is string => status !== null)
        .join(' · ');

    return (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Entity coverage">
                <div className="mb-2 mt-1 text-2xl font-semibold text-zinc-100">
                    {entityCoveragePercent === null ? 'N/A' : `${entityCoveragePercent}%`}
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800">
                    <div
                        className={`h-full ${coverageBarClass(entityCoveragePercent ?? 0)}`}
                        style={{ width: `${entityCoveragePercent ?? 0}%` }}
                    />
                </div>
                <div className="mt-1.5 text-xs text-zinc-500">
                    {coverage.entitiesWithBacktestsCount} / {coverage.totalEntitiesInUniverse} entities
                </div>
                {coverage.missingEntitiesCount > 0 ? (
                    <button className="mt-1 text-xs text-cyan-400 hover:text-cyan-300" type="button" onClick={onViewMissing}>
                        {coverage.missingEntitiesCount} missing · View list ↓
                    </button>
                ) : (
                    <div className="mt-1 text-xs text-zinc-500">At least one backtest per entity</div>
                )}
            </StatCard>

            <StatCard label="Population progress">
                <div className="mb-2 mt-1 text-2xl font-semibold text-zinc-100">
                    {population.completionPercent === null ? 'N/A' : `${population.completionPercent}%`}
                </div>
                <div
                    aria-label={
                        population.completionPercent === null
                            ? 'Backtest population progress unavailable'
                            : `Backtest population ${population.completionPercent}% complete`
                    }
                    aria-valuemax={100}
                    aria-valuemin={0}
                    aria-valuenow={population.completionPercent ?? undefined}
                    className="flex h-1.5 overflow-hidden rounded-full bg-zinc-800"
                    role="progressbar"
                >
                    <div
                        className="h-full bg-green-500"
                        style={{ width: getSegmentWidth(population.completedBacktests, population.expectedBacktests) }}
                    />
                    <div
                        className="h-full bg-cyan-500"
                        style={{ width: getSegmentWidth(population.activeBacktests, population.expectedBacktests) }}
                    />
                    <div
                        className="h-full bg-red-500"
                        style={{ width: getSegmentWidth(population.failedBacktests, population.expectedBacktests) }}
                    />
                </div>
                <div className="mt-1.5 text-xs text-zinc-500">
                    {population.completedBacktests} / {population.expectedBacktests} required backtests
                </div>
                {population.expectedBacktests === 0 ? (
                    <div className="mt-1 text-xs text-zinc-500">No valid combinations configured</div>
                ) : population.remainingBacktests === 0 ? (
                    <div className="mt-1 text-xs text-green-400">Complete</div>
                ) : (
                    <div className="mt-1 truncate text-xs text-zinc-500" title={populationStatus}>
                        {populationStatus}
                    </div>
                )}
            </StatCard>

            <StatCard label="Freshness">
                <div
                    className="mb-1 mt-1 truncate text-lg font-semibold text-zinc-100"
                    title={coverage.newestLastUpdateDateTime ?? undefined}
                >
                    {coverage.newestLastUpdateDateTime || 'N/A'}
                </div>
                {isStale ? (
                    <div className="text-xs text-red-400">Stale vs current scorecard</div>
                ) : (
                    <div className="truncate text-xs text-zinc-500">Oldest: {coverage.oldestLastUpdateDateTime || 'N/A'}</div>
                )}
            </StatCard>

            <StatCard label="Queue">
                <div className="mb-1 mt-1 text-2xl font-semibold text-zinc-100">
                    {activeQueueCount === 0 ? 'Idle' : `${activeQueueCount} active`}
                </div>
                <div className="text-xs text-zinc-500">{queueStatus ? queueStatus.environment : '—'}</div>
            </StatCard>
        </div>
    );
};

export default BacktestHealthStrip;
